import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './components/Button';
import Badge from './components/Badge';
import Card from './components/Card';
import { useToast } from './ToastContext';
import { getProjectsSummary } from './api';
import {
  FolderOpen,
  Clock,
  CheckCircle2,
  ArrowRight,
  Plus,
  Gauge,
  CalendarDays,
  Users,
  LayoutGrid,
  Table2,
  ArrowUpDown,
} from 'lucide-react';

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [viewMode, setViewMode] = useState('cards');
  const [sortConfig, setSortConfig] = useState({ key: 'createdDate', direction: 'desc' });
  const [globalLoading, setGlobalLoading] = useState(true);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    async function fetchProjects() {
      setGlobalLoading(true);
      setError('');
      try {
        const summary = await getProjectsSummary();

        setProjects(summary);

        const initialProjects = summary.map((s) => {
          const persistedScore = s.score_global && s.score_global > 0 ? s.score_global : null;
          const fallbackScore = s.calculated_score && s.calculated_score > 0 ? s.calculated_score : null;

          return {
            ...s,
            calculatedScore: persistedScore ?? fallbackScore ?? null,
            nbPagesAuditees: s.nb_pages_auditees || 0,
            score_global: s.score_global ?? 0,
          };
        });

        setProjectsData(initialProjects);
      } catch (e) {
        console.error('Erreur lors du chargement des projets:', e);
        setError(
          'Impossible de charger la liste des projets. Vérifiez que la base est à jour (migrations Alembic) ou réessayez plus tard.'
        );
      } finally {
        setGlobalLoading(false);
      }
    }
    fetchProjects();
  }, []);

  const handleAddProject = () => {
    navigate('/projects/create');
  };

  const handleViewProject = (project) => {
    navigate(`/projects/${project.slug}`);
  };

  const getProjectDisplayData = (project) => {
    const score = project.calculatedScore !== null && project.calculatedScore !== undefined
      ? project.calculatedScore
      : (project.score_global > 0 ? project.score_global : null);
    const hasScore = score !== null && score > 0;
    const hasAudit = project.date_dernier_audit !== null && project.date_dernier_audit !== undefined;
    const statusColor = hasScore
      ? (score >= 90 ? 'success' : score > 80 ? 'success' : score >= 60 ? 'accent' : score >= 50 ? 'warning' : 'error')
      : (hasAudit ? 'warning' : 'gray');
    const statusText = hasScore
      ? (score >= 90 ? 'Excellent' : score > 80 ? 'Très bon' : score >= 60 ? 'Bon' : score >= 50 ? 'Moyen' : score >= 40 ? 'À améliorer' : score > 0 ? 'Critique' : 'Non audité')
      : (hasAudit ? 'En cours' : 'Non audite');

    return {
      score,
      hasScore,
      statusColor,
      statusText,
      isSharedProject: Boolean(project.is_shared),
      lastAuditDate: formatDate(project.date_dernier_audit),
      createdDate: formatDate(project.date_created),
      lastAuditRaw: project.date_dernier_audit ? new Date(project.date_dernier_audit).getTime() : null,
      createdRaw: project.date_created ? new Date(project.date_created).getTime() : null,
    };
  };

  const sortProjects = (items) => {
    const sorted = [...items];
    sorted.sort((a, b) => {
      const da = getProjectDisplayData(a);
      const db = getProjectDisplayData(b);

      let av;
      let bv;
      switch (sortConfig.key) {
        case 'name':
          av = (a.name || '').toLowerCase();
          bv = (b.name || '').toLowerCase();
          break;
        case 'status':
          av = da.statusText;
          bv = db.statusText;
          break;
        case 'score':
          av = da.hasScore ? da.score : -1;
          bv = db.hasScore ? db.score : -1;
          break;
        case 'pages':
          av = a.nbPagesAuditees || 0;
          bv = b.nbPagesAuditees || 0;
          break;
        case 'lastAudit':
          av = da.lastAuditRaw ?? -1;
          bv = db.lastAuditRaw ?? -1;
          break;
        case 'createdDate':
        default:
          av = da.createdRaw ?? -1;
          bv = db.createdRaw ?? -1;
          break;
      }

      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedProjectsData = sortProjects(projectsData);

  const SortableHeader = ({ label, sortKey, align = 'left' }) => (
    <button
      type="button"
      onClick={() => handleSort(sortKey)}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide transition ${
        sortConfig.key === sortKey ? 'text-blue-gray-800' : 'text-blue-gray-500 hover:text-blue-gray-700'
      } ${align === 'center' ? 'mx-auto' : ''}`}
    >
      <span>{label}</span>
      <ArrowUpDown size={12} className={sortConfig.key === sortKey ? 'text-accent' : 'text-blue-gray-300'} />
    </button>
  );

  const scoredProjects = projectsData.filter((p) => (p.calculatedScore ?? p.score_global ?? 0) > 0);
  const auditedProjects = projectsData.filter((p) => (p.nbPagesAuditees || 0) > 0);
  const averageScore = scoredProjects.length
    ? Math.round(
      (scoredProjects.reduce((acc, p) => acc + (p.calculatedScore ?? p.score_global ?? 0), 0) / scoredProjects.length) * 10
    ) / 10
    : null;
  const totalAuditedPages = projectsData.reduce((acc, p) => acc + (p.nbPagesAuditees || 0), 0);

  // Formatage de la date
  function formatDate(dateString) {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const now = new Date();
      const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffMs = startOfNow - startOfDate;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays <= 0) return "Aujourd'hui";
      if (diffDays === 1) return 'Hier';
      if (diffDays <= 7) return `Il y a ${diffDays} jours`;
      if (diffDays <= 30) return `Il y a ${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return null;
    }
  }

  if (globalLoading) {
    return (
      <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-8">
        {/* Skeleton Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-9 w-48 bg-neutral-200 rounded-lg animate-pulse mb-2"></div>
            <div className="h-5 w-64 bg-neutral-100 rounded-lg animate-pulse"></div>
          </div>
          <div className="h-10 w-40 bg-neutral-200 rounded-lg animate-pulse"></div>
        </div>

        {/* Skeleton Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <div className="flex flex-col h-full">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-neutral-200 rounded-lg animate-pulse"></div>
                  <div className="flex-1">
                    <div className="h-6 w-32 bg-neutral-200 rounded-lg animate-pulse mb-2"></div>
                    <div className="h-8 w-16 bg-neutral-100 rounded-lg animate-pulse"></div>
                  </div>
                </div>
                <div className="h-6 w-20 bg-neutral-100 rounded-lg animate-pulse mb-4"></div>
                <div className="space-y-2 mb-4 flex-1">
                  <div className="h-4 w-full bg-neutral-100 rounded-lg animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-neutral-100 rounded-lg animate-pulse"></div>
                </div>
                <div className="pt-4 border-t border-neutral-200">
                  <div className="h-4 w-24 bg-neutral-100 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-8">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-3xl font-bold text-neutral-900 mb-1">
            Audits complets
          </h1>
          <p className="text-blue-gray-600 text-sm">
            {projects.length === 0
              ? 'Créez votre premier audit pour commencer'
              : `${projects.length} audit${projects.length > 1 ? 's' : ''} complet${projects.length > 1 ? 's' : ''}`
            }
          </p>
          {projects.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neutral-100 text-sm text-neutral-700">
                <Gauge size={14} className="text-accent" />
                <span>Score moyen : <span className="font-semibold">{averageScore != null ? `${averageScore}/100` : '—'}</span></span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neutral-100 text-sm text-neutral-700">
                <CheckCircle2 size={14} className="text-success" />
                <span>Projets audités : <span className="font-semibold">{auditedProjects.length}/{projectsData.length}</span></span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neutral-100 text-sm text-neutral-700">
                <FolderOpen size={14} className="text-neutral-500" />
                <span>Pages auditées : <span className="font-semibold">{totalAuditedPages}</span></span>
              </div>
            </div>
          )}
        </div>
        <Button onClick={handleAddProject} variant="primary">
          <Plus size={18} className="mr-2" />
          Ajouter un projet
        </Button>
      </div>

      {error && (
        <Card>
          <div className="text-center py-8 text-red-600">
            {error}
          </div>
        </Card>
      )}

      {projects.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-accent/10 rounded-2xl mb-6">
              <FolderOpen className="text-accent" size={48} />
            </div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">Aucun audit complet</h2>
            <p className="text-blue-gray-600 mb-6 text-center max-w-md">
              Créez votre premier audit complet pour analyser en profondeur un site web.
              Vous pourrez ensuite suivre les scores et les analyses ici.
            </p>
            <Button onClick={handleAddProject} variant="primary">
              <Plus size={18} className="mr-2" />
              Créer mon premier audit
            </Button>
          </div>
        </Card>
      ) : viewMode === 'cards' ? (
        <>
          <div className="flex justify-end -mt-2">
            <div className="inline-flex rounded-lg border border-neutral-200 p-1 bg-white">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 text-sm rounded-md transition inline-flex items-center gap-1.5 ${viewMode === 'cards' ? 'bg-accent text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
              >
                <LayoutGrid size={14} />
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-sm rounded-md transition inline-flex items-center gap-1.5 ${viewMode === 'table' ? 'bg-accent text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
              >
                <Table2 size={14} />
                Tableau
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectsData.map(project => {
            const {
              score,
              hasScore,
              statusColor,
              statusText,
              isSharedProject,
              lastAuditDate,
              createdDate,
            } = getProjectDisplayData(project);

              return (
                <Card
                  key={project.id}
                  className="group cursor-pointer hover:shadow-md transition-all hover:border-accent/40"
                  onClick={() => handleViewProject(project)}
                >
                  <div className="flex flex-col h-full">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <img
                        src={project.favicon_url || `https://www.google.com/s2/favicons?domain=${project.url || project.name}&sz=128`}
                        alt={project.name}
                        className="w-10 h-10 rounded-lg bg-white border border-neutral-200 shadow-sm"
                        onError={(e) => {
                          e.target.src = `https://www.google.com/s2/favicons?domain=${project.url || project.name}&sz=128`;
                        }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-neutral-900 text-base leading-tight truncate">
                            {project.name}
                          </h3>
                          {project.url && (
                            <p className="text-xs text-neutral-500 truncate mt-0.5" title={project.url}>
                              {project.url}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isSharedProject && (
                            <Badge color="accent">
                              <span className="inline-flex items-center gap-1">
                                <Users size={12} />
                                Partagé
                              </span>
                            </Badge>
                          )}
                          <Badge color={statusColor}>
                            {statusText}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-end justify-between mb-4">
                    <div className="text-xs text-neutral-500 uppercase tracking-wide">
                      Score SEO
                    </div>
                    {hasScore ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-accent leading-none">
                          {Math.round(score)}
                        </span>
                        <span className="text-sm text-neutral-500">/100</span>
                      </div>
                    ) : (
                      <span className="text-sm text-neutral-400">Non audité</span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4 flex-1">
                    <div className="rounded-lg bg-neutral-50 border border-neutral-100 px-3 py-2">
                      <div className="flex items-center gap-2 text-xs text-neutral-500 mb-0.5">
                        <CheckCircle2 size={12} className="text-success" />
                        Pages auditées
                      </div>
                      <div className="text-sm font-semibold text-neutral-900">
                        {project.nbPagesAuditees || 0}
                      </div>
                    </div>
                    <div className="rounded-lg bg-neutral-50 border border-neutral-100 px-3 py-2">
                      <div className="flex items-center gap-2 text-xs text-neutral-500 mb-0.5">
                        <Clock size={12} className="text-neutral-400" />
                        Dernier audit
                      </div>
                      <div className="text-sm font-semibold text-neutral-900 truncate">
                        {lastAuditDate || '—'}
                      </div>
                    </div>
                    <div className="rounded-lg bg-neutral-50 border border-neutral-100 px-3 py-2 sm:col-span-2">
                      <div className="flex items-center gap-2 text-xs text-neutral-500 mb-0.5">
                        <CalendarDays size={12} className="text-neutral-400" />
                        Date de création
                      </div>
                      <div className="text-sm font-semibold text-neutral-900">
                        {createdDate || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-neutral-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-600 group-hover:text-neutral-800 transition-colors">
                        Voir le détail du projet
                      </span>
                      <ArrowRight
                        size={16}
                        className="text-neutral-400 group-hover:text-accent transition-colors"
                      />
                    </div>
                  </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-end -mt-2">
            <div className="inline-flex rounded-lg border border-neutral-200 p-1 bg-white">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 text-sm rounded-md transition inline-flex items-center gap-1.5 ${viewMode === 'cards' ? 'bg-accent text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
              >
                <LayoutGrid size={14} />
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-sm rounded-md transition inline-flex items-center gap-1.5 ${viewMode === 'table' ? 'bg-accent text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
              >
                <Table2 size={14} />
                Tableau
              </button>
            </div>
          </div>
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-gray-100 bg-blue-gray-50/60">
                  <th className="text-left py-3 px-3"><SortableHeader label="Projet" sortKey="name" /></th>
                  <th className="text-left py-3 px-3"><SortableHeader label="Statut" sortKey="status" /></th>
                  <th className="text-center py-3 px-3"><SortableHeader label="Score" sortKey="score" align="center" /></th>
                  <th className="text-center py-3 px-3"><SortableHeader label="Pages" sortKey="pages" align="center" /></th>
                  <th className="text-left py-3 px-3"><SortableHeader label="Dernier audit" sortKey="lastAudit" /></th>
                  <th className="text-left py-3 px-3"><SortableHeader label="Creation" sortKey="createdDate" /></th>
                  <th className="py-3 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-gray-50">
                {sortedProjectsData.map((project) => {
                  const {
                    score,
                    hasScore,
                    statusColor,
                    statusText,
                    isSharedProject,
                    lastAuditDate,
                    createdDate,
                  } = getProjectDisplayData(project);

                  return (
                    <tr
                      key={project.id}
                      onClick={() => handleViewProject(project)}
                      className="hover:bg-blue-gray-50/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2 min-w-[220px]">
                          <img
                            src={project.favicon_url || `https://www.google.com/s2/favicons?domain=${project.url || project.name}&sz=128`}
                            alt={project.name}
                            className="w-7 h-7 rounded border border-neutral-200 bg-white"
                            onError={(e) => {
                              e.target.src = `https://www.google.com/s2/favicons?domain=${project.url || project.name}&sz=128`;
                            }}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-blue-gray-800 truncate">{project.name}</div>
                            <div className="text-xs text-blue-gray-500 truncate">{project.url || '—'}</div>
                          </div>
                          {isSharedProject && (
                            <Badge color="accent">
                              <span className="inline-flex items-center gap-1">
                                <Users size={12} />
                                Partage
                              </span>
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <Badge color={statusColor}>{statusText}</Badge>
                      </td>
                      <td className="py-3 px-3 text-center font-semibold text-blue-gray-900">
                        {hasScore ? `${Math.round(score)}/100` : '—'}
                      </td>
                      <td className="py-3 px-3 text-center text-blue-gray-700">{project.nbPagesAuditees || 0}</td>
                      <td className="py-3 px-3 text-blue-gray-700">{lastAuditDate || '—'}</td>
                      <td className="py-3 px-3 text-blue-gray-700">{createdDate || '—'}</td>
                      <td className="py-3 px-3 text-right">
                        <ArrowRight size={14} className="text-blue-gray-300" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
